// ============================================================================
// MODE COMPTOIR — l'état d'un ordinateur, pas d'une personne
// ============================================================================
// Le titulaire installe le mode comptoir une fois par machine, depuis ses
// Réglages. L'ordinateur reste ensuite ouvert en permanence sur l'emploi du
// temps, l'organisation du jour et les rendez-vous : personne ne tape rien, et
// toute l'équipe s'en sert.
//
// Le PIN n'ouvre plus ce mode — il en SORT. Le titulaire le saisit sur ce même
// poste pour retrouver la gestion, le temps qu'il lui faut.
//
// Trois durées de vie différentes, et c'est le cœur du sujet :
//   - le jeton du comptoir vit dans `localStorage`, 30 jours : c'est un meuble ;
//   - la session déverrouillée vit dans `sessionStorage` : fermer l'onglet
//     reverrouille, et un autre onglet du même navigateur ne l'hérite pas ;
//   - elle expire d'elle-même au bout de 30 min (le serveur ne signe pas plus
//     long), et se referme après 5 min sans un geste.
// ============================================================================

const COUNTER_KEY = 'officina_counter';
const UNLOCK_KEY = 'officina_unlock';

/** Sans geste pendant ce délai, la session de gestion se referme seule. */
export const IDLE_LOCK_MS = 5 * 60 * 1000;

interface UnlockState {
  token: string;
  /** Expiration du jeton, en ms epoch — donnée par le serveur. */
  expiresAt: number;
  /** Dernier geste du titulaire, en ms epoch. */
  lastActivity: number;
}

// `sessionStorage` peut lever (navigation privée verrouillée, stockage
// désactivé). Un poste comptoir ne doit pas afficher une page blanche pour ça.
function readUnlock(): UnlockState | null {
  try {
    const raw = sessionStorage.getItem(UNLOCK_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UnlockState;
  } catch {
    return null;
  }
}

function writeUnlock(state: UnlockState | null) {
  try {
    if (state) sessionStorage.setItem(UNLOCK_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(UNLOCK_KEY);
  } catch {
    /* rien à faire : on retombera simplement en mode verrouillé */
  }
}

export const counter = {
  /** Cet ordinateur est-il un poste comptoir ? */
  isPost(): boolean {
    try {
      return !!localStorage.getItem(COUNTER_KEY);
    } catch {
      return false;
    }
  },

  token(): string | null {
    try {
      return localStorage.getItem(COUNTER_KEY);
    } catch {
      return null;
    }
  },

  /** Le titulaire installe le mode comptoir sur cette machine. */
  install(token: string) {
    localStorage.setItem(COUNTER_KEY, token);
    writeUnlock(null);
  },

  /** Le titulaire rend l'ordinateur à un usage normal. */
  uninstall() {
    try {
      localStorage.removeItem(COUNTER_KEY);
    } catch {
      /* ignore */
    }
    writeUnlock(null);
  },

  /**
   * Jeton de la session de gestion, s'il y en a une de valide.
   *
   * Expirée ou trop longtemps inactive, elle est effacée ici même : le seul
   * endroit qui la lit est aussi celui qui la périme, on ne peut donc pas
   * l'oublier quelque part.
   */
  unlockedToken(): string | null {
    const state = readUnlock();
    if (!state) return null;
    const now = Date.now();
    if (now >= state.expiresAt || now - state.lastActivity > IDLE_LOCK_MS) {
      writeUnlock(null);
      return null;
    }
    return state.token;
  },

  isUnlocked(): boolean {
    return this.unlockedToken() !== null;
  },

  /** Ouvre la session de gestion après un PIN accepté. */
  setUnlocked(token: string, expiresInSeconds: number) {
    const now = Date.now();
    writeUnlock({
      token,
      expiresAt: now + expiresInSeconds * 1000,
      lastActivity: now,
    });
  },

  /** Un geste du titulaire repousse la fermeture automatique. */
  touch() {
    const state = readUnlock();
    if (!state) return;
    writeUnlock({ ...state, lastActivity: Date.now() });
  },

  /** Combien de temps reste-t-il avant fermeture ? (ms, 0 si verrouillé) */
  remainingMs(): number {
    const state = readUnlock();
    if (!state) return 0;
    const byExpiry = state.expiresAt - Date.now();
    const byIdle = state.lastActivity + IDLE_LOCK_MS - Date.now();
    return Math.max(0, Math.min(byExpiry, byIdle));
  },

  lock() {
    writeUnlock(null);
  },
};
